import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, message, Spin, Radio, InputNumber, Row, Col, Divider, Typography, Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { CONTRACT_API, INVOICE_API } from '../../constants/api';
import axios from '../../services/axiosConfig';
import dayjs from 'dayjs';

const { Text } = Typography;

const VIETNAM_HOLIDAYS = [
  { month: 1, day: 1, name: 'Tết Dương lịch' },
  { month: 4, day: 30, name: 'Ngày Giải phóng' },
  { month: 5, day: 1, name: 'Quốc tế Lao động' },
  { month: 9, day: 2, name: 'Quốc khánh' },
  { month: 9, day: 3, name: 'Quốc khánh (nghỉ bù)' },
  { month: 4, day: 18, name: 'Giỗ Tổ Hùng Vương (2024)' },
];

const TET_HOLIDAYS = [
  { year: 2024, startMonth: 2, startDay: 8, endMonth: 2, endDay: 14 },
  { year: 2025, startMonth: 1, startDay: 28, endMonth: 2, endDay: 3 },
  { year: 2026, startMonth: 2, startDay: 17, endMonth: 2, endDay: 23 },
  { year: 2027, startMonth: 2, startDay: 6, endMonth: 2, endDay: 12 },
  { year: 2028, startMonth: 1, startDay: 26, endMonth: 2, endDay: 1 },
  { year: 2029, startMonth: 2, startDay: 13, endMonth: 2, endDay: 19 },
  { year: 2030, startMonth: 2, startDay: 3, endMonth: 2, endDay: 9 },
];

const isTetHoliday = (date) => {
  const year = date.year();
  const month = date.month() + 1;
  const day = date.date();
  const tetInfo = TET_HOLIDAYS.find(tet => tet.year === year);
  if (!tetInfo) return false;

  if (
    (month === tetInfo.startMonth && day >= tetInfo.startDay && month === tetInfo.endMonth && day <= tetInfo.endDay) ||
    (month === tetInfo.startMonth && day >= tetInfo.startDay && tetInfo.startMonth !== tetInfo.endMonth) ||
    (month === tetInfo.endMonth && day <= tetInfo.endDay && tetInfo.startMonth !== tetInfo.endMonth)
  ) {
    return true;
  }
  return false;
};

const isFixedHoliday = (date) => {
  const month = date.month() + 1;
  const day = date.date();
  if (isTetHoliday(date)) return true;
  return VIETNAM_HOLIDAYS.some(holiday => holiday.month === month && holiday.day === day);
};

const isWeekend = (date) => {
  const day = date.day();
  return day === 0;
};

const isWorkingDay = (date) => {
  return !isWeekend(date) && !isFixedHoliday(date);
};

const calculateDueDate = (startDate, workingDays) => {
  let dueDate = startDate.clone();
  let daysAdded = 0;
  while (daysAdded < workingDays) {
    dueDate = dueDate.add(1, 'day');
    if (isWorkingDay(dueDate)) {
      daysAdded++;
    }
  }
  return dueDate;
};

const CreateInvoiceModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [fileList, setFileList] = useState([]);

  const fetchContracts = async () => {
    try {
      const { data } = await axios.get(CONTRACT_API.GET_ACTIVE_CONTRACTS);
      setContracts(data.data.map(contract => {
        const serviceFeeTotal = (contract.ServiceFee || 0) + (contract.InfrastructureFee || 0);
        return {
          value: contract.ContractID,
          label: `${contract.ContractID} - Xưởng: ${contract.Factory?.FactoryName || 'N/A'} - KH: ${contract.Tenant?.TenantName || 'N/A'} - Tiền thuê: ${
            new Intl.NumberFormat('vi-VN').format(contract.RentAmount || 0)
          } VND${serviceFeeTotal > 0 ? ` - Phí DV: ${new Intl.NumberFormat('vi-VN').format(serviceFeeTotal)} VND` : ''}`,
          contract: contract
        };
      }));
    } catch (error) {
      console.error('Lỗi khi tải danh sách hợp đồng:', error);
      message.error('Không thể tải danh sách hợp đồng');
    }
  };

  useEffect(() => {
    if (visible) {
      fetchContracts();
      form.resetFields();
      form.setFieldsValue({
        creationDate: dayjs(),
        isDebt: false,
        rentAmount: 0,
        serviceAmount: 0,
        electricityAmount: 0,
        waterAmount: 0,
        otherAmount: 0,
      });
      setTotalAmount(0);
      setFileList([]);
    }
  }, [visible, form]);

  const handleValuesChange = (changedValues, allValues) => {
    // Tự động tính tổng tiền
    const rent = allValues.rentAmount || 0;
    const service = allValues.serviceAmount || 0;
    const elec = allValues.electricityAmount || 0;
    const water = allValues.waterAmount || 0;
    const other = allValues.otherAmount || 0;
    
    setTotalAmount(rent + service + elec + water + other);
  };

  const handleContractChange = (value) => {
    const contractItem = contracts.find(c => c.value === value);
    const contract = contractItem?.contract;
    setSelectedContract(contract);
    
    if (contract) {
      const rentAmt = contract.RentAmount || 0;
      const serviceAmt = (contract.ServiceFee || 0) + (contract.InfrastructureFee || 0);
      
      form.setFieldsValue({
        factoryId: contract.FactoryID,
        tenantId: contract.TenantID,
        rentAmount: rentAmt,
        serviceAmount: serviceAmt
      });
      
      // Tính lại tổng
      handleValuesChange({}, form.getFieldsValue());

      const paymentDueDate = contract.Tenant?.PaymentDueDate;
      if (paymentDueDate && paymentDueDate > 0) {
        const creationDate = form.getFieldValue('creationDate') || dayjs();
        const dueDate = calculateDueDate(creationDate, paymentDueDate);
        form.setFieldsValue({
          paymentDueDate: dueDate
        });
      }
    }
  };

  const isDayjs = (date) => date && date.$isDayjsObject;
  const handleReadingDateChange = (date) => {
    if (!date || !isDayjs(date)) {
      return;
    }
    
    if (selectedContract && selectedContract.Tenant?.PaymentDueDate) {
      const workingDaysRequired = selectedContract.Tenant.PaymentDueDate;
      const dueDate = calculateDueDate(date, workingDaysRequired);
      form.setFieldsValue({ paymentDueDate: dueDate });
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (!values.contractId) {
        throw new Error('Contract ID không được để trống');
      }

      setLoading(true);

      const formData = new FormData();
      formData.append('contractId', values.contractId);
      formData.append('invoiceCode', values.invoiceCode || '');
      formData.append('period', values.period);
      if (values.creationDate) formData.append('creationDate', values.creationDate.format('YYYY-MM-DD'));
      if (values.paymentDueDate) formData.append('paymentDueDate', values.paymentDueDate.format('YYYY-MM-DD'));
      formData.append('rentAmount', values.rentAmount || 0);
      formData.append('serviceAmount', values.serviceAmount || 0);
      formData.append('electricityAmount', values.electricityAmount || 0);
      formData.append('waterAmount', values.waterAmount || 0);
      formData.append('otherAmount', values.otherAmount || 0);
      formData.append('totalAmount', totalAmount);
      formData.append('isDebt', values.isDebt ? true : false);
      formData.append('note', values.note || '');

      fileList.forEach(file => {
        formData.append('invoiceFiles', file);
      });

      await axios.post(INVOICE_API.CREATE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      message.success('Tạo hóa đơn thành công');
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('Lỗi khi tạo hóa đơn:', error.message);
      message.error(error.message || 'Không thể tạo hóa đơn');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Modal
      title="Tạo hóa đơn tổng hợp (Hàng tháng)"
      visible={visible}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleValuesChange}
          initialValues={{
            creationDate: dayjs(),
            isDebt: false,
            rentAmount: 0,
            serviceAmount: 0,
            electricityAmount: 0,
            waterAmount: 0,
            otherAmount: 0
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="contractId"
                label="Hợp đồng"
                rules={[{ required: true, message: 'Vui lòng chọn hợp đồng' }]}
              >
                <Select
                  placeholder="Chọn hợp đồng"
                  options={contracts}
                  onChange={handleContractChange}
                  showSearch
                  filterOption={(input, option) => {
                    const searchValue = input.toLowerCase();
                    const labelText = option.label.toLowerCase();
                    const contractId = option.contract?.ContractID?.toString().toLowerCase() || '';
                    const factoryName = option.contract?.Factory?.FactoryName?.toLowerCase() || '';
                    const tenantName = option.contract?.Tenant?.TenantName?.toLowerCase() || '';
                    
                    return labelText.includes(searchValue) || 
                           contractId.includes(searchValue) || 
                           factoryName.includes(searchValue) || 
                           tenantName.includes(searchValue);
                  }}
                  optionFilterProp="children"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="invoiceCode"
                label="Mã hoá đơn"
                rules={[]}
              >
                <Input placeholder="Tự động tạo (Vd: HD00001) nếu để trống" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="period"
                label="Kỳ hóa đơn (Tháng/Năm)"
                rules={[{ required: true, message: 'Vui lòng nhập kỳ hóa đơn' }]}
              >
                <Input placeholder="Vd: 05/2024" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="creationDate"
                label="Ngày tạo hóa đơn"
                rules={[{ required: true, message: 'Vui lòng chọn ngày tạo hóa đơn' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="DD/MM/YYYY"
                  onChange={handleReadingDateChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentDueDate"
                label="Hạn thanh toán"
                rules={[{ required: true, message: 'Vui lòng chọn hạn thanh toán' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="DD/MM/YYYY"
                  placeholder="Chọn hạn thanh toán"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Chi tiết các khoản thu</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="rentAmount" label="Tiền thuê xưởng">
                <InputNumber 
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="serviceAmount" label="Phí dịch vụ & hạ tầng">
                <InputNumber 
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="electricityAmount" label="Tiền điện">
                <InputNumber 
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="waterAmount" label="Tiền nước sạch">
                <InputNumber 
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="otherAmount" label="Tiền nước thải">
                <InputNumber 
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ padding: '10px 15px', background: '#f5f5f5', borderRadius: '4px', width: '100%' }}>
                <Text strong>Tổng cộng: </Text>
                <Text strong type="danger" style={{ fontSize: '18px' }}>
                  {new Intl.NumberFormat('vi-VN').format(totalAmount)} ₫
                </Text>
              </div>
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="isDebt"
                label="Có phải là công nợ không"
                rules={[{ required: true, message: 'Vui lòng chọn có phải là công nợ không' }]}
              >
                <Radio.Group>
                  <Radio value={true}>Công nợ</Radio>
                  <Radio value={false}>Không phải công nợ</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="note" label="Ghi chú">
                <Input.TextArea rows={3} placeholder="Ví dụ: Hóa đơn tháng 05/2024" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="File đính kèm">
                <Upload
                  multiple
                  beforeUpload={(file) => {
                    setFileList([...fileList, file]);
                    return false;
                  }}
                  onRemove={(file) => {
                    const index = fileList.indexOf(file);
                    const newFileList = fileList.slice();
                    newFileList.splice(index, 1);
                    setFileList(newFileList);
                  }}
                  fileList={fileList}
                >
                  <Button icon={<UploadOutlined />}>Chọn file (PDF, hình ảnh)</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateInvoiceModal;